import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Download, Upload, Check, X, ChevronDown, ChevronUp, Star, Home, Briefcase } from 'lucide-react';
import useComparisonStore from '../store/comparisonStore';
import { contentVariants, fadeInVariants } from '../utils/animations';

/**
 * ComparisonTable component
 * Displays a side-by-side comparison of selected packages
 */
const ComparisonTable = ({ packages = [], onSelectPackage = () => {} }) => {
  const { formatSpeed } = useComparisonStore();
  const [expandedFeatures, setExpandedFeatures] = useState(false);
  
  // If no packages, show empty state
  if (packages.length === 0) {
    return (
      <motion.div 
        className="bg-gray-800 rounded-xl p-6 text-center"
        variants={contentVariants}
      >
        <p className="text-gray-400 mb-4">No packages selected for comparison</p>
        <p className="text-sm text-gray-500">Select packages from the list to compare them side by side</p>
      </motion.div>
    );
  }
  
  // Get all unique features across all packages
  const allFeatures = new Set();
  packages.forEach(pkg => {
    if (pkg.features) {
      pkg.features.forEach(feature => allFeatures.add(feature));
    }
  });
  
  // Default features if none specified
  const defaultFeatures = [
    'No throttling or shaping',
    'Free installation',
    'Free router',
    'Month-to-month contract'
  ];
  
  // Use package features or default features
  const featuresToDisplay = allFeatures.size > 0
    ? Array.from(allFeatures).filter(feature =>
        !feature.toLowerCase().includes('symmetrical') &&
        !feature.toLowerCase().includes('asymmetrical'))
    : defaultFeatures.filter(feature =>
        !feature.toLowerCase().includes('symmetrical') &&
        !feature.toLowerCase().includes('asymmetrical'));
  
  // Limit features display unless expanded
  const displayFeatures = expandedFeatures 
    ? featuresToDisplay 
    : featuresToDisplay.slice(0, 3);
  
  return (
    <motion.div 
      className="bg-gray-800 rounded-xl overflow-hidden"
      variants={contentVariants}
    >
      <div className="p-4 border-b border-gray-700">
        <h3 className="text-lg font-semibold text-white">Package Comparison</h3>
        <p className="text-sm text-gray-400">Compare features side by side</p>
      </div>
      
      {/* Scrollable table container */}
      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead className="bg-gray-900/50">
            <tr>
              <th className="p-4 text-sm font-medium text-gray-400 border-b border-r border-gray-700 min-w-[140px]">
                Package Details
              </th>
              
              {packages.map((pkg, index) => (
                <th key={index} className="p-4 text-white border-b border-gray-700 min-w-[180px]">
                  <div className="flex items-center">
                    {pkg.operator?.logo ? (
                      <img 
                        src={pkg.operator.logo} 
                        alt={pkg.operatorName} 
                        className="h-5 w-5 object-contain mr-2"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.style.display = 'none';
                        }}
                      />
                    ) : (
                      <div className={`h-5 w-5 rounded-full mr-2 flex items-center justify-center ${
                        pkg.connectionType === 'FTTB'
                          ? 'bg-blue-600/20'
                          : 'bg-purple-600/20'
                      }`}>
                        {pkg.connectionType === 'FTTB'
                          ? <Briefcase className="h-3 w-3 text-blue-400" />
                          : <Home className="h-3 w-3 text-purple-400" />
                        }
                      </div>
                    )}
                    <span className="font-medium">{pkg.operatorName}</span>
                  </div>
                  <div className="text-xs text-gray-400 mt-1">
                    {pkg.connectionType === 'FTTB' ? 'Business Fibre' : 'Home Fibre'}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          
          <tbody>
            {/* Speed Row */}
            <tr className="border-b border-gray-700">
              <td className="p-4 text-sm font-medium text-gray-400 border-r border-gray-700">
                <div className="flex items-center">
                  <Download className="h-4 w-4 mr-2" />
                  Download Speed
                </div>
              </td>
              
              {packages.map((pkg, index) => {
                const originalPkg = pkg.originalPackage || pkg;
                return (
                  <td key={index} className="p-4">
                    <div className="text-lg font-bold text-white">
                      {formatSpeed(originalPkg["Download speed (kbps)"])}
                    </div>
                  </td>
                );
              })}
            </tr>
            
            {/* Upload Speed Row */}
            <tr className="border-b border-gray-700">
              <td className="p-4 text-sm font-medium text-gray-400 border-r border-gray-700">
                <div className="flex items-center">
                  <Upload className="h-4 w-4 mr-2" />
                  Upload Speed
                </div>
              </td>
              
              {packages.map((pkg, index) => {
                const originalPkg = pkg.originalPackage || pkg;
                return (
                  <td key={index} className="p-4">
                    <div className="text-lg font-bold text-white">
                      {formatSpeed(originalPkg["Upload speed (kbps)"])}
                    </div>
                  </td>
                );
              })}
            </tr>
            
            {/* Price Row */}
            <tr className="border-b border-gray-700 bg-gray-900/30">
              <td className="p-4 text-sm font-medium text-gray-400 border-r border-gray-700">
                <div className="flex items-center">
                  <Star className="h-4 w-4 mr-2" />
                  Monthly Price
                </div>
              </td>
              
              {packages.map((pkg, index) => (
                <td key={index} className="p-4">
                  <div className="text-lg font-bold text-white">
                    R{pkg.Price}
                  </div>
                  <div className="text-xs text-gray-400">per month</div>
                </td>
              ))}
            </tr>
            
            {/* Symmetrical Row */}
            <tr className="border-b border-gray-700">
              <td className="p-4 text-sm font-medium text-gray-400 border-r border-gray-700">
                Symmetrical
              </td>
              
              {packages.map((pkg, index) => {
                const originalPkg = pkg.originalPackage || pkg;
                const isSymmetrical = originalPkg["Download speed (kbps)"] && originalPkg["Upload speed (kbps)"] &&
                                      originalPkg["Download speed (kbps)"] === originalPkg["Upload speed (kbps)"];
                return (
                  <td key={index} className="p-4">
                    <div className="flex items-center">
                      {isSymmetrical ? (
                        <Check className="h-5 w-5 text-green-400" />
                      ) : (
                        <X className="h-5 w-5 text-red-400" />
                      )}
                    </div>
                  </td>
                );
              })}
            </tr>
            
            {/* Features Rows */}
            {displayFeatures.map((feature, featureIndex) => (
              <tr key={featureIndex} className="border-b border-gray-700">
                <td className="p-4 text-sm font-medium text-gray-400 border-r border-gray-700">
                  {feature}
                </td>
                
                {packages.map((pkg, pkgIndex) => {
                  // Check if package has this feature
                  const hasFeature = pkg.features 
                    ? pkg.features.includes(feature)
                    : defaultFeatures.includes(feature); // Assume default features
                  
                  return (
                    <td key={pkgIndex} className="p-4">
                      <div className="flex items-center">
                        {hasFeature ? (
                          <Check className="h-5 w-5 text-green-400" />
                        ) : (
                          <X className="h-5 w-5 text-red-400" />
                        )}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
            
            {/* Show more/less features button */}
            {featuresToDisplay.length > 3 && (
              <tr>
                <td colSpan={packages.length + 1} className="p-2 text-center">
                  <motion.button
                    className="text-sm text-purple-400 hover:text-purple-300 flex items-center justify-center mx-auto py-2"
                    onClick={() => setExpandedFeatures(!expandedFeatures)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {expandedFeatures ? (
                      <>
                        <ChevronUp className="h-4 w-4 mr-1" />
                        Show Less Features
                      </>
                    ) : (
                      <>
                        <ChevronDown className="h-4 w-4 mr-1" />
                        Show More Features ({featuresToDisplay.length - 3} more)
                      </>
                    )}
                  </motion.button>
                </td>
              </tr>
            )}
            
            {/* Action Row */}
            <tr className="bg-gray-900/30">
              <td className="p-4 text-sm font-medium text-gray-400 border-r border-gray-700">
                Action
              </td>
              
              {packages.map((pkg, index) => (
                <td key={index} className="p-4">
                  <motion.button
                    className={`px-4 py-2 text-white rounded-lg text-sm font-medium transition-colors ${
                      pkg.connectionType === 'FTTB'
                        ? 'bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700'
                        : 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700'
                    }`}
                    onClick={() => onSelectPackage(pkg)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Check Availability
                  </motion.button>
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </motion.div>
  );
};

export default ComparisonTable;